<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  type GroupedReaction,
  type MessageWithReply,
  bigEmojiFontSize,
  extractImageUrlsFromText,
  getMessageStatusIcon,
  getMessageStyles,
  getMessageClipboardText,
  parseQuoteFromContent,
  shouldShowMessageText,
  stripImageUrlsFromText,
  useChatStoreCore,
} from '../../chatModules'
import { splitTextWithMentions } from '@/modules/chat-matrix-fast/utils/mentions'
import {
  buildMentionLabelByMxid,
  openChatForMentionedUser,
} from '@/modules/chat-matrix-fast/composables/useMentionNavigation'
import MessageReplyContext from './MessageReplyContext.vue'
import ReplyPreviewThumbnail from './ReplyPreviewThumbnail.vue'
import MessageAttachments from './MessageAttachments.vue'
import MatrixAttachmentMedia from './MatrixAttachmentMedia.vue'
import MessageReactions from './MessageReactions.vue'
import MessageLinkPreview from './MessageLinkPreview.vue'
import {
  findReplySourceMessage,
  getReplyPreviewFromMessage,
  resolveReplyToForMessage,
} from '@/modules/chat-matrix-fast/utils/replyPreview'
import { peekInReplyToEventId } from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { isGroupInviteUrl, stripGroupInviteUrls, GROUP_INVITE_UI_ENABLED } from '@/modules/chat-matrix-fast/utils/groupInviteLink'
import { useChatReactionContext } from '@/modules/chat-matrix-fast/composables/useChatReactionContext'
import { formatRussianTime } from '@/utils/russianDateFormatter'
import { DELETED_MESSAGE_TEXT } from '@/modules/chat-matrix-fast/constants/chat'
import {
  extractFirstUrl,
  hasMarkdownMarkup,
  markdownToSafeHtml,
} from '@/modules/chat-matrix-fast/utils/messageRichText'
import type { ChatType } from '@/modules/chat-matrix-fast/types/IChat'

interface Props {
  message: MessageWithReply
  messageId: string | number
  isMine: boolean
  isLastInGroup: boolean
  isLastInChat: boolean
  isHovered: boolean
  isPinned?: boolean
  reactions: GroupedReaction[]
  replySenderName: string
  /** Фамилия и инициалы — только для первого чужого сообщения в группе */
  senderName?: string
  senderNameColorClass?: string
  chatId?: number | string
  chatType?: ChatType
  canPin?: boolean
  canForward?: boolean
  canReply?: boolean
  canOpenProfile?: boolean
  selectionMode?: boolean
  isSelected?: boolean
  canSelect?: boolean
}

interface Emits {
  (e: 'reaction-click', reactionTypeId: string | number, isMyReaction: boolean): void
  (e: 'trigger-enter', event: MouseEvent): void
  (e: 'trigger-leave'): void
  (e: 'trigger-click', event: MouseEvent): void
  (e: 'menu-click', event: MouseEvent): void
  (e: 'quote-click', messageId: string | number, matrixEventId?: string): void
  (e: 'toggle-select'): void
  (e: 'mouseenter'): void
  (e: 'mouseleave'): void
}

const props = withDefaults(defineProps<Props>(), {
  canPin: false,
  canForward: false,
  canReply: true,
  canOpenProfile: true,
  selectionMode: false,
  isSelected: false,
  canSelect: false,
})
const emit = defineEmits<Emits>()

const reactionContext = useChatReactionContext()
const coreChatStore = useChatStoreCore()
const isOpeningMention = ref(false)

/** Три точки только если в меню есть хотя бы одно действие */
const hasMenuActions = computed(() => {
  const msg = props.message
  const hasEventId = Boolean(msg.matrix_event_id)

  if (props.isMine && !msg.is_deleted) return true
  if (props.canReply) return true
  if (props.canForward && !msg.is_deleted && !msg.is_system && hasEventId) return true
  if (props.canPin && !msg.is_deleted && hasEventId) return true
  if (props.canOpenProfile && !props.isMine) return true
  if (getMessageClipboardText(msg)) return true

  return false
})

const activeMatrixRoomId = computed(() => {
  const chatId = props.chatId
  if (chatId == null) {
    return coreChatStore.currentChat?.matrix_room_id ?? null
  }

  if (coreChatStore.currentChat?.id === chatId) {
    return coreChatStore.currentChat.matrix_room_id ?? null
  }

  return coreChatStore.chats.find(chat => chat.id === chatId)?.matrix_room_id ?? null
})

function onWrapperClick(event: MouseEvent) {
  if (!props.selectionMode || !props.canSelect) {
    return
  }

  event.preventDefault()
  event.stopPropagation()
  emit('toggle-select')
}

function onContextMenu(event: MouseEvent) {
  if (props.selectionMode) {
    event.preventDefault()
    return
  }

  if (!hasMenuActions.value) {
    return
  }

  event.preventDefault()
  emit('menu-click', event)
}

const handleTriggerEnter = (event: MouseEvent) => {
  if (reactionContext) {
    reactionContext.onTriggerEnter(props.messageId, event)
    return
  }
  emit('trigger-enter', event)
}

const handleTriggerClick = (event: MouseEvent) => {
  if (reactionContext) {
    reactionContext.onTriggerClick(props.messageId, event)
    return
  }
  emit('trigger-click', event)
}

const handleTriggerLeave = () => {
  if (reactionContext) {
    reactionContext.onTriggerLeave(props.messageId)
    return
  }
  emit('trigger-leave')
}

const handleReactionPillClick = (reactionTypeId: string | number, isMyReaction: boolean) => {
  if (reactionContext) {
    reactionContext.onReactionPillClick(props.messageId, reactionTypeId, isMyReaction)
    return
  }

  emit('reaction-click', reactionTypeId, isMyReaction)
}

const messageStyles = computed(() => getMessageStyles(props.isMine))
const statusIcon = computed(() => getMessageStatusIcon(props.message.feedback))
const isDeleted = computed(() => props.message.is_deleted === true)

const parsedContent = computed(() => {
  if (isDeleted.value) {
    return { quote: null, remainingText: DELETED_MESSAGE_TEXT }
  }

  const content = props.message.message || props.message.content || ''

  return parseQuoteFromContent(content)
})

const displayReplyTo = computed(() =>
  resolveReplyToForMessage(coreChatStore.messages, props.message),
)

const quotedSourceMessage = computed(() => {
  if (parsedContent.value.quote) {
    return findReplySourceMessage(
      coreChatStore.messages,
      props.message,
      parsedContent.value.quote,
    )
  }

  return displayReplyTo.value
})

const quoteReplyPreview = computed(() => {
  if (!parsedContent.value.quote) {
    return null
  }

  const source = quotedSourceMessage.value
  if (source) {
    return getReplyPreviewFromMessage({
      content: source.content,
      message: source.content,
      attachments: source.attachments,
    })
  }

  return {
    text: parsedContent.value.quote.text,
  }
})

const hasContent = computed(() => {
  if (isDeleted.value) {
    return true
  }

  return !!(
    props.message.message
    || props.message.content
    || (props.message.attachments && props.message.attachments.length > 0)
    || displayReplyTo.value
    || parsedContent.value.quote
  )
})

const messageText = computed(() =>
  parsedContent.value.remainingText || props.message.message || '',
)

const displayMessageText = computed(() =>
  stripGroupInviteUrls(stripImageUrlsFromText(messageText.value)),
)

const messageTextSegments = computed(() =>
  splitTextWithMentions(displayMessageText.value, {
    mentionUserIds: props.message.mention_user_ids,
    labelByMxid: buildMentionLabelByMxid(
      activeMatrixRoomId.value,
      props.message.mention_user_ids,
    ),
  }),
)

const hasMentionSegments = computed(() =>
  messageTextSegments.value.some(segment => segment.type === 'mention'),
)

const linkPreviewUrl = computed(() => {
  if (isDeleted.value) {
    return null
  }
  return extractFirstUrl(messageText.value)
})

const isInviteLinkPreview = computed(() => {
  if (!GROUP_INVITE_UI_ENABLED) {
    return false
  }
  const url = linkPreviewUrl.value
  return Boolean(url && isGroupInviteUrl(url))
})

const richHtml = computed(() => {
  if (isDeleted.value || hasMentionSegments.value) {
    return ''
  }
  if (!hasMarkdownMarkup(displayMessageText.value) && !extractFirstUrl(displayMessageText.value)) {
    return ''
  }
  return markdownToSafeHtml(displayMessageText.value)
})

const onRichTextClick = (event: MouseEvent) => {
  if (!GROUP_INVITE_UI_ENABLED) {
    return
  }
  const target = event.target
  if (!(target instanceof HTMLAnchorElement)) {
    return
  }
  const href = target.getAttribute('href') || ''
  if (!isGroupInviteUrl(href)) {
    return
  }
  event.preventDefault()
  event.stopPropagation()
  const wrap = (event.currentTarget as HTMLElement)?.closest('.message-wrapper')
  const previewBtn = wrap?.querySelector('.invite-preview') as HTMLButtonElement | null
  previewBtn?.click()
}

const openMentionChat = async (mxid?: string) => {
  if (!mxid || isOpeningMention.value || isDeleted.value) {
    return
  }

  isOpeningMention.value = true
  try {
    await openChatForMentionedUser(mxid)
  } finally {
    isOpeningMention.value = false
  }
}

const inlineImageUrls = computed(() => {
  if (props.message.attachments?.length) {
    return []
  }

  return extractImageUrlsFromText(messageText.value)
})

const showMessageText = computed(() => {
  if (isInviteLinkPreview.value && !displayMessageText.value.trim()) {
    return false
  }

  return shouldShowMessageText(displayMessageText.value, props.message.attachments)
})

const emojiFontSize = computed(() => {
  if (
    isDeleted.value
    || props.message.attachments?.length
    || displayReplyTo.value
    || parsedContent.value.quote
    || inlineImageUrls.value.length
  ) {
    return null
  }

  return bigEmojiFontSize(displayMessageText.value)
})

const isBigEmoji = computed(() => emojiFontSize.value != null)

const statusIconColor = computed(() => {
  if (statusIcon.value.color === 'success') {
    return 'success'
  }

  return isBigEmoji.value ? undefined : statusIcon.value.color
})

const bubbleStyles = computed(() => {
  if (isBigEmoji.value) {
    return {
      backgroundColor: 'transparent',
      color: 'inherit',
      border: 'none',
      boxShadow: 'none',
    }
  }

  return messageStyles.value
})

const messageTextStyles = computed(() => {
  const size = emojiFontSize.value
  if (size == null) {
    return undefined
  }

  return {
    fontSize: `${size}px`,
    lineHeight: '1.15',
    letterSpacing: '0.04em',
  }
})

const handleQuoteClick = (e: Event) => {
  e.stopPropagation()

  const replyTo = displayReplyTo.value ?? props.message.reply_to
  const replyToId = typeof replyTo === 'number' || typeof replyTo === 'string'
    ? replyTo
    : replyTo && typeof replyTo === 'object' && 'id' in replyTo
      ? replyTo.id
      : undefined

  const replyToEventId = typeof replyTo === 'object' && replyTo
    && typeof replyTo.matrix_event_id === 'string'
    ? replyTo.matrix_event_id
    : undefined

  const replyPreviewText = typeof replyTo === 'object' && replyTo
    ? (replyTo.content || replyTo.message || '')
    : ''

  const source = quotedSourceMessage.value
    || findReplySourceMessage(
      coreChatStore.messages,
      props.message,
      {
        sender: props.replySenderName || '',
        text: replyPreviewText || quoteReplyPreview.value?.text || '',
      },
    )

  const fromMatrixRelation = peekInReplyToEventId(
    activeMatrixRoomId.value,
    props.message.matrix_event_id,
  )

  const matrixEventId
    = ([
      props.message.reply_message_id,
      props.message.reply_to_id,
      replyToEventId,
      replyToId,
      fromMatrixRelation,
      source?.matrix_event_id,
    ].find(value => typeof value === 'string' && value.startsWith('$')) as string | undefined)
    || source?.matrix_event_id
    || fromMatrixRelation

  const targetMessageId
    = matrixEventId
    || props.message.reply_message_id
    || props.message.reply_to_id
    || replyToId
    || source?.id
    || source?.matrix_event_id

  if (!targetMessageId) {
    useFeedbackStore().showToast({
      type: 'info',
      message: 'Исходное сообщение недоступно',
      time: 3000,
    })
    return
  }

  emit('quote-click', targetMessageId, matrixEventId)
}
</script>

<template>
  <div
    class="message-wrapper"
    :class="[
      isMine ? 'message-wrapper--mine' : 'message-wrapper--theirs',
      {
        'message-wrapper--selection': selectionMode,
        'message-wrapper--selected': selectionMode && isSelected,
        'message-wrapper--selectable': selectionMode && canSelect,
      },
    ]"
    :data-message-index="messageId"
    :data-matrix-event-id="message.matrix_event_id || undefined"
    @mouseenter="$emit('mouseenter')"
    @mouseleave="$emit('mouseleave')"
    @click.capture="onWrapperClick"
    @contextmenu="onContextMenu"
  >
    <div
      v-if="selectionMode && canSelect"
      class="message-select-check"
      :class="{ 'message-select-check--active': isSelected }"
      aria-hidden="true"
    />
    <div
      v-else-if="selectionMode"
      class="message-select-check message-select-check--spacer"
      aria-hidden="true"
    />

    <div
      v-if="hasContent"
      class="message-content-block"
    >
      <div
        class="chat-content text-body-1"
        :class="[
          isLastInGroup ? 'mb-1' : 'mb-2',
          isBigEmoji
            ? 'chat-content--big-emoji'
            : [isMine ? 'chat-right' : 'chat-left', 'py-2 px-4 elevation-2'],
        ]"
        :style="bubbleStyles"
      >
        <div
          v-if="senderName"
          class="message-sender-name"
          :class="senderNameColorClass"
        >
          {{ senderName }}
        </div>

        <MessageReplyContext
          v-if="!isDeleted && displayReplyTo && !parsedContent.quote"
          :reply-to="displayReplyTo"
          :sender-name="replySenderName"
          :is-mine="isMine"
          :source-attachments="quotedSourceMessage?.attachments"
          @click="handleQuoteClick"
        />

        <div
          v-if="!isDeleted && message.forward?.original_sender_display"
          class="message-forward-label"
          :class="isMine ? 'chat-right' : 'chat-left'"
        >
          Переслано от {{ message.forward.original_sender_display }}
        </div>

        <div
          v-if="!isDeleted && parsedContent.quote"
          class="message-quote"
          :class="isMine ? 'message-quote--mine' : 'message-quote--theirs'"
          @click="handleQuoteClick"
        >
          <div class="message-quote-line" />
          <ReplyPreviewThumbnail
            v-if="quoteReplyPreview?.thumbnail"
            :src="quoteReplyPreview.thumbnail"
          />
          <div class="message-quote-content">
            <div class="message-quote-sender">
              {{ parsedContent.quote.sender }}
            </div>
            <div class="message-quote-text">
              {{ quoteReplyPreview?.text || parsedContent.quote.text }}
            </div>
          </div>
        </div>

        <div :class="{ 'big-emoji-stack': isBigEmoji }">
          <div
            v-if="showMessageText"
            class="mb-0 message-text"
            :class="{
              'mt-2': !isDeleted && (displayReplyTo || parsedContent.quote || senderName),
              'message-text--deleted': isDeleted,
              'message-text--big-emoji': isBigEmoji,
            }"
            :style="messageTextStyles"
          >
            <span
              v-if="richHtml"
              class="message-rich-text"
              v-html="richHtml"
              @click="onRichTextClick"
            />
            <template v-else>
              <template
                v-for="(segment, index) in messageTextSegments"
                :key="`${index}-${segment.type}`"
              >
                <a
                  v-if="segment.type === 'mention' && segment.mxid"
                  href="#"
                  class="message-mention message-mention--link"
                  @click.prevent.stop="openMentionChat(segment.mxid)"
                >{{ segment.value.replace(/^@/, '') }}</a>
                <span
                  v-else-if="segment.type === 'mention'"
                  class="message-mention"
                >{{ segment.value.replace(/^@/, '') }}</span>
                <template v-else>{{ segment.value }}</template>
              </template>
            </template>
          </div>

          <MessageLinkPreview
            v-if="!isDeleted && linkPreviewUrl"
            :url="linkPreviewUrl"
            class="message-link-preview-wrap"
          />

          <div
            v-if="!isDeleted && inlineImageUrls.length"
            class="inline-image-urls"
            :class="{ 'mt-2': showMessageText || displayReplyTo || parsedContent.quote }"
          >
            <MatrixAttachmentMedia
              v-for="(url, index) in inlineImageUrls"
              :key="`${url}-${index}`"
              :src="url"
              kind="image"
            />
          </div>

          <MessageAttachments
            v-if="!isDeleted && message.attachments && message.attachments.length > 0"
            :attachments="message.attachments"
            :message-text="message.message"
          />

          <div class="message-footer">
            <MessageReactions
              v-if="!isDeleted"
              :message-id="messageId"
              :reactions="reactions"
              @reaction-click="handleReactionPillClick"
            />
            <span class="message-meta-inline">
              <VIcon
                v-if="isPinned"
                icon="ri-pushpin-2-fill"
                size="12"
                class="message-meta-pin"
              />
              <span
                v-if="message.is_edited"
                class="message-meta-edited"
              >изм.</span>
              <span class="message-meta-time">{{ formatRussianTime(message.time) }}</span>
              <VIcon
                v-if="isMine"
                size="14"
                :color="statusIconColor"
                :class="{ 'message-status-icon--read': statusIcon.color === 'success' }"
              >
                {{ statusIcon.icon }}
              </VIcon>
            </span>
          </div>
        </div>
      </div>

      <Transition name="reaction-trigger-fade">
        <div
          v-show="!selectionMode && isHovered && !isDeleted"
          class="message-actions"
        >
          <button
            v-if="hasMenuActions"
            class="message-menu-trigger"
            type="button"
            aria-label="Меню сообщения"
            @click.stop="emit('menu-click', $event)"
          >
            <VIcon
              icon="ri-more-2-fill"
              size="16"
            />
          </button>
          <button
            class="reaction-trigger"
            type="button"
            aria-label="Добавить реакцию"
            @mouseenter="handleTriggerEnter"
            @mouseleave="handleTriggerLeave"
            @click.stop="handleTriggerClick"
          >
            <VIcon
              icon="ri-thumb-up-line"
              size="16"
            />
          </button>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.message-wrapper--selection {
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 100%;
}

.message-wrapper--selectable {
  cursor: pointer;
}

.message-wrapper--selected .chat-content:not(.chat-content--big-emoji) {
  box-shadow: inset 0 0 0 1.5px rgba(var(--v-theme-primary), 0.55);
}

.message-select-check {
  flex-shrink: 0;
  box-sizing: border-box;
  width: 20px;
  height: 20px;
  margin-top: 10px;
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.28);
  border-radius: 50%;
  background: transparent;
  position: relative;
}

.message-select-check--active {
  border-color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-primary));
}

.message-select-check--active::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 3px;
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 1.5px 1.5px 0;
  transform: rotate(45deg);
}

.message-select-check--spacer {
  border-color: transparent;
}

.message-wrapper--theirs.message-wrapper--selection {
  flex-direction: row-reverse;
}

.message-text,
.message-quote-text {
  white-space: pre-line;
  word-break: break-word;
}

.message-rich-text {
  white-space: pre-wrap;

  :deep(a) {
    color: inherit;
    text-decoration: underline;
  }

  :deep(u) {
    text-decoration: underline;
  }

  :deep(code) {
    font-size: 0.92em;
    padding: 0.05em 0.3em;
    border-radius: 4px;
    background: rgba(var(--v-theme-on-surface), 0.08);
  }
}

.message-sender-name {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.25;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.avatar-color-1 { color: #667eea; }
  &.avatar-color-2 { color: #f5576c; }
  &.avatar-color-3 { color: #4facfe; }
  &.avatar-color-4 { color: #43e97b; }
  &.avatar-color-5 { color: #fa709a; }
  &.avatar-color-6 { color: #5ecfc9; }
  &.avatar-color-7 { color: #ff9a9e; }
  &.avatar-color-8 { color: #fcb69f; }
}

.message-mention {
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
}

.message-mention--link {
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.message-text--deleted {
  font-style: italic;
  opacity: 0.7;
}

.message-text--big-emoji {
  white-space: pre-wrap;
}

.chat-content {
  position: relative;
  max-width: 100%;
}

.chat-content:not(.chat-content--big-emoji) {
  min-width: 70px;
}

.message-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
  min-width: 0;
  max-width: 100%;
}

.message-meta-inline {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
  gap: 4px;
  line-height: 1;
  user-select: none;
  pointer-events: none;
}

.chat-content:not(.chat-content--big-emoji) :deep(.reactions-bar) {
  margin-top: 0;
}

.message-meta-time,
.message-meta-edited {
  font-size: 11px;
  letter-spacing: 0.2px;
  color: rgba(var(--v-theme-on-surface), 0.45);
}

.message-meta-pin {
  color: rgba(var(--v-theme-on-surface), 0.45);
}

.message-forward-label {
  font-size: 0.75rem;
  font-weight: 500;
  margin-bottom: 4px;
  color: rgba(var(--v-theme-primary), 0.9);
}

.chat-content.chat-content--big-emoji {
  padding: 0;
  box-shadow: none;
  background: transparent;
  border: none;
  min-width: 0;
}

.big-emoji-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: max-content;

  .message-footer {
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
    margin-top: 4px;
  }

  .message-meta-inline {
    margin-left: 0;
    padding: 2px 6px;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(8px);
  }

  .message-meta-time,
  .message-meta-edited,
  .message-meta-pin {
    color: rgba(255, 255, 255, 0.92);
  }

  :deep(.v-icon) {
    color: rgba(255, 255, 255, 0.92);
  }

  :deep(.v-icon.text-success),
  :deep(.message-status-icon--read) {
    color: rgb(var(--v-theme-success));
  }

  :deep(.reactions-bar) {
    margin-top: 0;
    justify-content: flex-end;
  }
}

.message-link-preview-wrap {
  clear: both;
  margin-top: 6px;
}

.inline-image-urls {
  display: flex;
  flex-direction: column;
  gap: 8px;
  clear: both;
}

.message-content-block {
  position: relative;
}

.message-actions {
  position: absolute;
  top: -5px;
  bottom: -5px;
  min-height: 54px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 24px;
  pointer-events: none;
  z-index: 11;
}

.message-actions > * {
  pointer-events: auto;
}

.message-wrapper--mine .message-actions {
  left: -28px;
  right: auto;
}

.message-wrapper--theirs .message-actions {
  right: -28px;
  left: auto;
}

.message-actions .reaction-trigger,
.message-actions .message-menu-trigger {
  position: static;
  left: auto;
  right: auto;
  top: auto;
  bottom: auto;
  transform: none;
}
</style>
